// src/pages/MeetingBooked.tsx
//
// Native replacement for the "Meeting Booked Conversion" Google Form + Zapier flow
// (the GForm titled "CBAM FUND Event - 2 - Booked"). Investment-team gated (route +
// server RLS). Lookup-driven (funds/houses/people) — no hardcoded lists. Submits to
// the meeting-book edge fn via an offline-safe outbox; identity emails are resolved
// server-side, never in the browser. Phase 1: Asana task only (calendar_mode:'none',
// the fixed Zap still bridges the calendar); native calendar lands in a later phase.

import { useEffect, useMemo, useState } from "react";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useMeetingLookups } from "@/hooks/useMeetingLookups";
import { enqueueMeeting, postMeeting, removeMeeting, drainMeetings, MeetingPostError } from "@/lib/meetings/meetingOutbox";
import { useIsOnline } from "@/hooks/useIsOnline";
import { Loader2 } from "lucide-react";

const NEW_HOUSE = "__new__";

const MeetingBooked = () => {
  // SUBSCRIBE, do not read the module cache. useIsOnline's cache is only updated while a
  // component calling THIS HOOK is mounted (its setOnline call sites all live in the hook's
  // effect), and nothing else on this route mounts it — so getIsOnline() here would be frozen
  // at module-evaluation time. See the note in the submit catch below.
  const online = useIsOnline();
  const { toast } = useToast();
  const { user } = useAuth();
  const { funds, fundHouses, people, isLoading } = useMeetingLookups();

  const [fundHouseId, setFundHouseId] = useState("");
  const [newHouseName, setNewHouseName] = useState("");
  const [fundId, setFundId] = useState("");
  const [alreadyHold, setAlreadyHold] = useState("");
  const [meetingDate, setMeetingDate] = useState("");
  const [meetingTime, setMeetingTime] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [personResponsibleId, setPersonResponsibleId] = useState("");
  const [attendeeIds, setAttendeeIds] = useState<string[]>([]);
  const [gcalEntryName, setGcalEntryName] = useState("");
  const [meetingType, setMeetingType] = useState("");
  const [sendingLink, setSendingLink] = useState(""); // yes | no | na
  const [location, setLocation] = useState("");
  const [asanaTaskId, setAsanaTaskId] = useState("");
  const [notes, setNotes] = useState("");
  const [documents, setDocuments] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const isNewHouse = fundHouseId === NEW_HOUSE;
  const selectedFund = useMemo(() => funds.find((f) => f.id === fundId), [funds, fundId]);

  // Drain any queued (offline) submissions on mount + whenever the tab comes online.
  useEffect(() => {
    void drainMeetings();
    const onOnline = () => void drainMeetings();
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, []);

  const toggleAttendee = (id: string, checked: boolean) => {
    setAttendeeIds((prev) => (checked ? [...prev, id] : prev.filter((a) => a !== id)));
  };

  const resetForm = () => {
    setFundHouseId(""); setNewHouseName(""); setFundId(""); setAlreadyHold("");
    setMeetingDate(""); setMeetingTime(""); setDurationMinutes(""); setPersonResponsibleId("");
    setAttendeeIds([]); setGcalEntryName(""); setMeetingType(""); setSendingLink("");
    setLocation(""); setAsanaTaskId(""); setNotes(""); setDocuments([]);
  };

  const buildBooking = () => {
    const house = fundHouses.find((h) => h.id === fundHouseId);
    return {
      form_kind: "booked",
      owner_staff_id: personResponsibleId || null,
      person_responsible_research_staff_id: personResponsibleId || null,
      cbam_attendee_staff_ids: attendeeIds,
      fund_id: fundId || null,
      fund_name: selectedFund?.fund_name ?? null,
      fund_class: selectedFund?.fund_class ?? null,
      isin: selectedFund?.isin ?? null,
      ocf_pct: selectedFund?.ocf_pct ?? null,
      fund_manager: selectedFund?.fund_manager ?? null,
      fund_house_id: isNewHouse ? null : (fundHouseId || null),
      fund_house_name: isNewHouse ? newHouseName : (house?.name ?? null),
      is_new_house: isNewHouse,
      new_house_name: isNewHouse ? newHouseName : null,
      already_hold: alreadyHold === "yes" ? true : alreadyHold === "no" ? false : null,
      meeting_type: meetingType || null,
      meeting_date: meetingDate || null,
      meeting_time: meetingTime || null,
      duration_minutes: durationMinutes ? Number(durationMinutes) : null,
      gcal_entry_name: gcalEntryName || null,
      location: location || null,
      cbam_hosts_link: sendingLink === "yes",
      conferencing_provider: null,
      external_asana_task_id: asanaTaskId || null,
      requester_notes: notes || null,
    };
  };

  const handleSubmit = async () => {
    // Minimal required-field validation (mirrors the GForm's required questions).
    if (!fundHouseId || (isNewHouse && !newHouseName)) {
      toast({ title: "Investment house required", variant: "destructive" }); return;
    }
    if (!personResponsibleId) {
      toast({ title: "CBAM person responsible for research is required", variant: "destructive" }); return;
    }
    if (!meetingDate) {
      toast({ title: "Meeting date is required", variant: "destructive" }); return;
    }

    const key = crypto.randomUUID();
    const booking = buildBooking();
    const attachment_keys: string[] = [];
    setSubmitting(true);
    try {
      // Upload advance documents to the private research-attachments bucket (best-effort,
      // requires online). Path is uid-prefixed to satisfy the bucket's write RLS.
      if (documents.length > 0 && user) {
        for (const file of documents) {
          const path = `${user.id}/meetings/${key}/${file.name}`;
          const { error } = await supabase.storage
            .from("research-attachments")
            .upload(path, file, { upsert: false });
          if (!error) attachment_keys.push(path);
        }
      }

      // Phase 2: 'harvest' = create the shared-calendar event now from the form + enrich it later
      // with the fund manager's join link. GO-LIVE ORDER: only deploy this frontend to production
      // AFTER Sam has set the calendar secrets/OAuth AND disabled the old Zap's calendar-creation
      // node — otherwise the native writer and the Zap both create events (duplicates). Until the
      // calendar identity is configured the native writer fails closed (booking + Asana still land).
      const payload = { booking, calendar_mode: "harvest", attachment_keys };
      // Stage first (survives a mid-request failure / offline), then attempt the post.
      enqueueMeeting(key, payload);
      const data = await postMeeting(key, payload);
      removeMeeting(key);

      const failed = data?.partial ? Object.keys(data.partial).length > 0 : false;
      toast({
        title: failed ? "Booked with warnings" : "Meeting booked",
        description: data?.asana_task_url
          ? "Asana task created; the meeting is being added to the shared calendar."
          : failed
            ? "Saved, but some steps reported issues — check the Asana board."
            : "Submitted.",
      });
      if (data?.asana_task_url) window.open(data.asana_task_url, "_blank", "noopener");
      resetForm();
    } catch (e) {
      // WHAT THIS CATCH MAY AND MAY NOT CLAIM.
      //
      // It used to render "Saved offline" for EVERY failure without ever reading `e`, so a
      // server refusal told the analyst their regulated booking was safely queued when it had
      // just been rejected. A first fix then guarded on getIsOnline() — copied from
      // src/components/capture/CaptureSheet.tsx:330 — but copied the GUARD WITHOUT THE
      // SUBSCRIPTION, and that is the trap worth recording:
      //
      //   useIsOnline's `onlineCache` (src/hooks/useIsOnline.ts:16) is a module-level value whose
      //   ONLY writer is setOnline(), and every call site of setOnline (:74,:76,:88,:90,:91) sits
      //   inside useIsOnline()'s useEffect. The cache therefore only tracks connectivity while a
      //   component calling the HOOK is mounted. CaptureSheet does exactly that at :130. These
      //   three pages did not, and nothing in their route (partnerOpsRoutes -> ProtectedRoute ->
      //   Layout -> PartnerNavbar) mounts it either — the only global mount is OfflineBanner in
      //   src/components/mobile/MobileShell.tsx:237. So getIsOnline() here was frozen at whatever
      //   navigator.onLine said at module-evaluation time, i.e. effectively always true, and the
      //   offline branch was dead code.
      //
      // So this page now SUBSCRIBES (`const online = useIsOnline()` in the component) and
      // classifies on the typed error the outbox throws.
      const status = e instanceof MeetingPostError ? e.status : null;
      const message = e instanceof Error ? e.message : "Submission failed.";

      // No HTTP status reached us at all (transport died), or we know we are offline: this is the
      // one case that has genuinely been queued for later.
      if (status === null || !online) {
        toast({
          title: "Saved offline",
          description:
            "Your booking is queued and will submit automatically when you're back online.",
        });
      } else {
        // The server answered and refused. Say so, and name the status — the page CAN now, because
        // meetingOutbox throws MeetingPostError carrying `.status` (meetingOutbox.ts:152-163,
        // thrown at :198). Earlier comments here claimed the status was unavailable; that was
        // true of an older postMeeting and is no longer.
        //
        // ⚠️ THE ITEM IS DELIBERATELY LEFT IN THE OUTBOX. An earlier fix deleted it, which
        // contradicted the outbox's own stated policy (meetingOutbox.ts:213-214: an item "KEEPS
        // its payload and its place in the outbox, never deleted, because the payload is work
        // somebody typed and this is the only copy of it") and threw away RETRYABLE failures
        // (5xx/408/429) before drainMeetings could apply the bounded retry it now implements.
        // A permanent 4xx is parked by the drain on its first pass (:268,:286) and never
        // re-posted, so leaving it queued cannot produce a duplicate booking.
        toast({
          variant: "destructive",
          title: "Meeting not booked",
          description:
            `${message} (HTTP ${status}). Your answers are still on this form; nothing has been ` +
            `filed. It stays in the outbox and will not be re-sent automatically if the server ` +
            `refused it permanently.`,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/5 to-accent/5 py-12 px-4 sm:px-6 lg:px-8">
      <SEO
        title="Book a Fund Manager Meeting — CBAM"
        description="Record a confirmed fund-manager meeting: creates the Asana task and calendar entry for the Cape Berkshire Asset Management investment team."
        path="/meeting-booked"
        noindex
      />
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-2xl font-bold">Meeting Booked</CardTitle>
            <CardDescription>
              Record a confirmed fund-manager meeting. Creates the Asana task; the calendar entry is
              handled by the existing flow during the transition.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-8">
              {/* Fund + house */}
              <section className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Investment House / Provider *</Label>
                  <Select value={fundHouseId} onValueChange={setFundHouseId}>
                    <SelectTrigger><SelectValue placeholder={isLoading ? "Loading…" : "Select investment house"} /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {fundHouses.map((h) => (<SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>))}
                      <SelectItem value={NEW_HOUSE}>Other (new, not on list)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {isNewHouse && (
                  <div>
                    <Label className="text-sm font-medium">New house name *</Label>
                    <Input value={newHouseName} onChange={(e) => setNewHouseName(e.target.value)} placeholder="Enter the new fund house name" />
                  </div>
                )}
                <div>
                  <Label className="text-sm font-medium">Fund Name</Label>
                  <p className="text-xs text-muted-foreground mb-1">Leave blank if an existing Asana task already holds the fund details.</p>
                  <Select value={fundId} onValueChange={setFundId}>
                    <SelectTrigger><SelectValue placeholder={isLoading ? "Loading…" : "Select fund"} /></SelectTrigger>
                    <SelectContent className="max-h-60">
                      {funds.map((f) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.fund_name}{f.fund_manager ? ` — ${f.fund_manager}` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {selectedFund && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Class {selectedFund.fund_class ?? "—"} · OCF {selectedFund.ocf_pct ?? "—"} · ISIN {selectedFund.isin ?? "—"}
                    </p>
                  )}
                </div>
                <div>
                  <Label className="text-sm font-medium">Already hold?</Label>
                  <RadioGroup value={alreadyHold} onValueChange={setAlreadyHold} className="mt-2 flex gap-6">
                    <div className="flex items-center space-x-2"><RadioGroupItem value="yes" id="hold-yes" /><Label htmlFor="hold-yes">Yes</Label></div>
                    <div className="flex items-center space-x-2"><RadioGroupItem value="no" id="hold-no" /><Label htmlFor="hold-no">No</Label></div>
                  </RadioGroup>
                </div>
              </section>

              {/* Timing */}
              <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium">Meeting date *</Label>
                  <Input type="date" value={meetingDate} onChange={(e) => setMeetingDate(e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm font-medium">Meeting time</Label>
                  <Input type="time" value={meetingTime} onChange={(e) => setMeetingTime(e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm font-medium">Duration (mins)</Label>
                  <Input type="number" min={0} step={5} value={durationMinutes} onChange={(e) => setDurationMinutes(e.target.value)} placeholder="e.g. 45" />
                </div>
              </section>

              {/* People */}
              <section className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">CBAM person responsible for research *</Label>
                  <Select value={personResponsibleId} onValueChange={setPersonResponsibleId}>
                    <SelectTrigger><SelectValue placeholder={isLoading ? "Loading…" : "Select person"} /></SelectTrigger>
                    <SelectContent>
                      {people.map((p) => (<SelectItem key={p.id} value={p.id}>{p.display_name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-medium">CBAM attendees</Label>
                  <div className="mt-2 space-y-2">
                    {people.map((p) => (
                      <div key={p.id} className="flex items-center space-x-2">
                        <Checkbox id={`att-${p.id}`} checked={attendeeIds.includes(p.id)} onCheckedChange={(c) => toggleAttendee(p.id, c as boolean)} />
                        <Label htmlFor={`att-${p.id}`}>{p.display_name}</Label>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* Meeting details */}
              <section className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">GCalendar entry name</Label>
                  <p className="text-xs text-muted-foreground mb-1">The title of the calendar invite (used to match the manager's event).</p>
                  <Input value={gcalEntryName} onChange={(e) => setGcalEntryName(e.target.value)} placeholder="e.g. CBAM x Baillie Gifford — Global Alpha" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Meeting type</Label>
                  <RadioGroup value={meetingType} onValueChange={setMeetingType} className="mt-2">
                    <div className="flex items-center space-x-2"><RadioGroupItem value="fund_meeting" id="mt-fund" /><Label htmlFor="mt-fund">Fund meeting (F2F or Webex)</Label></div>
                    <div className="flex items-center space-x-2"><RadioGroupItem value="webinar" id="mt-web" /><Label htmlFor="mt-web">Webinar / online fund presentation / event</Label></div>
                  </RadioGroup>
                </div>
                <div>
                  <Label className="text-sm font-medium">If the manager didn't create the invite, are we sending the link?</Label>
                  <RadioGroup value={sendingLink} onValueChange={setSendingLink} className="mt-2 flex gap-6">
                    <div className="flex items-center space-x-2"><RadioGroupItem value="yes" id="link-yes" /><Label htmlFor="link-yes">Yes</Label></div>
                    <div className="flex items-center space-x-2"><RadioGroupItem value="no" id="link-no" /><Label htmlFor="link-no">No</Label></div>
                    <div className="flex items-center space-x-2"><RadioGroupItem value="na" id="link-na" /><Label htmlFor="link-na">N/A</Label></div>
                  </RadioGroup>
                </div>
                <div>
                  <Label className="text-sm font-medium">Location (F2F only)</Label>
                  <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Address / room" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Existing Asana task ID (optional)</Label>
                  <p className="text-xs text-muted-foreground mb-1">If a task already exists, we update it instead of creating a new one.</p>
                  <Input value={asanaTaskId} onChange={(e) => setAsanaTaskId(e.target.value)} placeholder="Asana task GID" />
                </div>
              </section>

              {/* Notes + docs */}
              <section className="space-y-4">
                <div>
                  <Label className="text-sm font-medium">Notes</Label>
                  <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Any context for the meeting…" />
                </div>
                <div>
                  <Label className="text-sm font-medium">Documents sent in advance</Label>
                  <Input
                    type="file"
                    multiple
                    accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx"
                    onChange={(e) => setDocuments(Array.from(e.target.files ?? []))}
                    className="mt-1"
                  />
                  {documents.length > 0 && (
                    <p className="text-xs text-muted-foreground mt-1">{documents.length} file(s) selected</p>
                  )}
                </div>
              </section>

              <div className="flex justify-end pt-4 border-t">
                <Button onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">
                  {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {submitting ? "Booking…" : "Book meeting"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default MeetingBooked;
